/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import { POLL_OPTIONS } from '../../lib/pricing.ts'

/**
 * The public pricing poll (D13 / LANDING_PAGE.md §6).
 *
 * Results are visible WITHOUT voting — showing the tally only after you commit
 * would be a dark pattern, and the page's whole posture is candour.
 */

type Tally = Record<string, number>

export function Poll() {
  const [tally, setTally] = useState<Tally>({})
  const [choice, setChoice] = useState<string>('')
  const [writeIn, setWriteIn] = useState('')
  const [voted, setVoted] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    fetch('/api/poll')
      .then((r) => r.json())
      .then((d) => setTally(d.tally ?? {}))
      .catch(() => {/* tally just stays empty — not worth an error state */})
  }, [])

  const total = Object.values(tally).reduce((a, b) => a + b, 0)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!choice || busy) return
    setBusy(true)
    try {
      const res = await fetch('/api/poll', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ option: choice, writeIn: writeIn || undefined }),
      })
      const data = await res.json()
      if (data.tally) setTally(data.tally)
      setVoted(true)
    } catch {
      setVoted(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <form onSubmit={submit}>
        <fieldset disabled={voted}>
          <legend className='sr-only'>Which pricing model would you pay for?</legend>
          <ul className='space-y-2'>
            {POLL_OPTIONS.map((opt) => {
              const count = tally[opt.id] ?? 0
              const pct = total ? Math.round((count / total) * 100) : 0
              return (
                <li key={opt.id}>
                  <label
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
                      choice === opt.id
                        ? 'border-[var(--brand)] bg-[var(--surface-raised)]'
                        : 'border-[var(--edge)] bg-[var(--surface-raised)] hover:border-[var(--ink-muted)]'
                    } ${voted ? 'cursor-default' : ''}`}
                  >
                    <input
                      type='radio'
                      name='price-vote'
                      value={opt.id}
                      checked={choice === opt.id}
                      onChange={() => setChoice(opt.id)}
                      className='size-4 accent-[var(--brand)]'
                    />
                    <span className='flex-1 text-sm'>{opt.label}</span>
                    <span className='shrink-0 font-mono text-xs text-[var(--ink-muted)]'>
                      {pct}%
                    </span>
                  </label>
                  <div
                    className='mt-1 h-1 overflow-hidden rounded-full bg-[var(--surface-raised)]'
                    role='presentation'
                  >
                    <div
                      className='h-full rounded-full bg-[var(--brand)] transition-[width]'
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              )
            })}
          </ul>

          {choice === 'none' && (
            <div className='mt-3'>
              <label htmlFor='write-in' className='text-sm font-medium'>
                What would you rather pay?
              </label>
              <textarea
                id='write-in'
                value={writeIn}
                onChange={(e) => setWriteIn(e.currentTarget.value)}
                rows={3}
                maxLength={500}
                className='mt-1 w-full rounded-xl border border-[var(--edge)] bg-[var(--surface-raised)] p-3 text-sm focus:outline-none'
                placeholder='Usage-based, team seats, pay-what-you-want…'
              />
            </div>
          )}
        </fieldset>

        {!voted && (
          <button
            type='submit'
            disabled={!choice || busy}
            className='mt-4 rounded-full bg-[var(--brand)] px-6 py-3 font-semibold text-[var(--brand-ink)] disabled:opacity-50'
          >
            {busy ? 'Recording…' : 'Vote'}
          </button>
        )}
      </form>

      <p className='mt-4 text-sm text-[var(--ink-muted)]' role='status'>
        {voted
          ? `✓ Recorded — thank you. ${total} ${total === 1 ? 'vote' : 'votes'} so far.`
          : total
          ? `${total} ${total === 1 ? 'vote' : 'votes'} so far. Results are public either way.`
          : 'No votes yet. Be the first.'}
      </p>
    </div>
  )
}
